// New evaluation cases of an agent (ADR 0035), without writing JSON: the model drafts cases from the agent's
// instructions, tools and existing cases (a project agent; the agents API's draft-cases, nothing stored) and the
// person keeps the ones they want, or the person writes one with checks picked from a list. Either way the cases
// are saved as `agent` tests of the app through the host's evals port (one change).
import { Loader2, Sparkles, X } from 'lucide-react';
import * as React from 'react';
import type { EvalSuite } from './Evaluations.tsx';
import { useAgentsHost } from './host.tsx';
import { checkText, type EvalCheck } from './rules.ts';
import { btnGhost, btnPrimary, input, messageOf, unwrap } from './ui.tsx';

const DRAFT = '/agents/{key}/draft-cases' as const;

/** A check the person picks by hand: its words, and how it is stored. */
const KINDS: {
  id: string;
  label: string;
  needs: 'none' | 'text' | 'tool' | 'seconds' | 'tokens';
  make: (v: string) => EvalCheck;
}[] = [
  {
    id: 'not-refused',
    label: 'is not a refusal',
    needs: 'none',
    make: () => ({ path: '$.refused', op: 'falsy' }),
  },
  {
    id: 'not-empty',
    label: 'is not empty',
    needs: 'none',
    make: () => ({ path: '$.answer', op: 'min_length', value: 1 }),
  },
  {
    id: 'has',
    label: 'answer contains',
    needs: 'text',
    make: (v) => ({ path: '$.answer', op: 'matches', value: pattern(v) }),
  },
  {
    id: 'has-not',
    label: 'answer does not contain',
    needs: 'text',
    make: (v) => ({ path: '$.answer', op: 'not_matches', value: pattern(v) }),
  },
  {
    id: 'calls',
    label: 'calls the tool',
    needs: 'tool',
    make: (v) => ({ path: '$.tool_names', op: 'contains', value: v }),
  },
  {
    id: 'calls-not',
    label: 'does not call the tool',
    needs: 'tool',
    make: (v) => ({ path: '$.tool_names', op: 'not_contains', value: v }),
  },
  {
    id: 'within',
    label: 'answers within (seconds)',
    needs: 'seconds',
    make: (v) => ({ path: '$.duration_ms', op: 'lte', value: Math.round(Number(v) * 1000) }),
  },
  {
    id: 'tokens',
    label: 'uses at most (tokens)',
    needs: 'tokens',
    make: (v) => ({ path: '$.usage.total_tokens', op: 'lte', value: Math.round(Number(v)) }),
  },
];

const pattern = (v: string) => `/${v.trim().replace(/[.*+?^${}()|[\]\\/]/g, '\\$&')}/i`;
const slug = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60);

interface Draft {
  name: string;
  prompt: string;
  expect: EvalCheck[];
  similarTo: string | null;
  keep: boolean;
}

export function AddCases({
  subject,
  suites,
  suite,
  tools = [],
  onSaved,
  onClose,
}: {
  /** `agent:<key>` (the model can draft) or `persona:<key>` (by hand only). */
  subject: string;
  suites: EvalSuite[];
  /** The suite open now: new cases go into it (none yet: `default`). */
  suite: string;
  /** The agent's tools, for the "calls the tool" checks (absent: typed by hand). */
  tools?: { name: string; note: string }[];
  /** The saved cases' keys, and whether to run them now. */
  onSaved: (keys: string[], run: boolean) => void;
  onClose: () => void;
}) {
  const host = useAgentsHost();
  const agent = subject.startsWith('agent:') ? subject.slice(6) : null;
  const [mode, setMode] = React.useState<'ai' | 'hand'>(agent ? 'ai' : 'hand');
  const target = suite || 'default';
  const [hint, setHint] = React.useState('');
  const [count, setCount] = React.useState(4);
  const [drafts, setDrafts] = React.useState<Draft[] | null>(null);
  const [busy, setBusy] = React.useState<'' | 'draft' | 'save'>('');
  const [err, setErr] = React.useState('');
  const [name, setName] = React.useState('');
  const [prompt, setPrompt] = React.useState('');
  const [rules, setRules] = React.useState<{ kind: string; value: string }[]>([
    { kind: 'not-refused', value: '' },
    { kind: 'not-empty', value: '' },
  ]);
  const existing = suites.flatMap((s) => s.cases.map((c) => ({ name: c.name, prompt: c.prompt })));
  const taken = new Set(suites.flatMap((s) => s.cases.map((c) => c.key)));

  const draft = async () => {
    if (!agent) return;
    setBusy('draft');
    setErr('');
    try {
      const r = await unwrap(
        host.client.fetch.POST(DRAFT, {
          params: { path: { key: agent } },
          body: { hint, count, existing },
        }),
      );
      setDrafts(r.cases.map((c) => ({ ...c, expect: c.expect as EvalCheck[], keep: !c.similarTo })));
      if (!r.cases.length)
        setErr('The model proposed no case it could check. Try again or say what to check.');
    } catch (e) {
      setErr(messageOf(e));
    } finally {
      setBusy('');
    }
  };

  const save = async (cases: { name: string; prompt: string; expect: EvalCheck[] }[], run: boolean) => {
    if (!host.evals?.addCases || !cases.length) return;
    setBusy('save');
    setErr('');
    try {
      const keys: string[] = [];
      const tests = cases.map((c) => {
        const base = slug(`${subject.split(':')[1]}-${c.name}`) || 'case';
        let key = base;
        for (let n = 2; taken.has(key) || keys.includes(key); n++) key = `${base}-${n}`;
        keys.push(key);
        return {
          key,
          name: c.name,
          kind: 'agent' as const,
          spec: {
            [subject.startsWith('agent:') ? 'agent' : 'persona']: subject.split(':')[1],
            suite: target,
            prompt: c.prompt,
            timeout_ms: 170000,
            expect: c.expect,
          },
        };
      });
      await host.evals.addCases({ tests });
      onSaved(keys, run);
    } catch (e) {
      setErr(messageOf(e));
    } finally {
      setBusy('');
    }
  };

  /** A check whose value is missing (or not a number of seconds): it is marked, and Save waits for it. */
  const missing = (r: { kind: string; value: string }) => {
    const k = KINDS.find((x) => x.id === r.kind)!;
    if (k.needs === 'none') return false;
    if (k.needs === 'seconds' || k.needs === 'tokens') return !(Number(r.value) > 0);
    return !r.value.trim();
  };
  const handReady = !!name.trim() && !!prompt.trim() && rules.length > 0 && !rules.some(missing);
  const handCase = () => {
    const expect = rules
      .map((r) => {
        const k = KINDS.find((x) => x.id === r.kind)!;
        return missing(r) ? null : k.make(r.value.trim());
      })
      .filter((c): c is EvalCheck => !!c);
    return { name: name.trim(), prompt: prompt.trim(), expect };
  };
  const kept = (drafts ?? []).filter((d) => d.keep);

  return (
    <div className="rounded-lg border border-nav-active/60 p-3 grid gap-3" data-testid="evals-add">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-page-title">New cases</span>
        <div className="ml-auto flex gap-1">
          {agent ? (
            <button
              type="button"
              className={mode === 'ai' ? btnPrimary : btnGhost}
              data-testid="evals-add-mode-ai"
              onClick={() => setMode('ai')}
            >
              <Sparkles size={14} /> Generate with AI
            </button>
          ) : null}
          <button
            type="button"
            className={mode === 'hand' ? btnPrimary : btnGhost}
            data-testid="evals-add-mode-hand"
            onClick={() => setMode('hand')}
          >
            Write by hand
          </button>
          <button type="button" className={btnGhost} aria-label="Close" onClick={onClose}>
            <X size={14} />
          </button>
        </div>
      </div>
      {mode === 'ai' ? (
        <div className="grid gap-2">
          <label className="grid gap-1 text-xs text-page-muted">
            What should the cases check? Optional: empty, the AI picks from the agent’s instructions and
            tools.
            <textarea
              className={input}
              rows={2}
              data-testid="evals-add-hint"
              placeholder="e.g. it never saves unless asked; it does not invent figures"
              value={hint}
              onChange={(e) => setHint(e.target.value)}
            />
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-xs text-page-muted">
              How many{' '}
              <select className={input} value={count} onChange={(e) => setCount(Number(e.target.value))}>
                {[2, 3, 4, 5, 6].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className={btnPrimary}
              data-testid="evals-add-generate"
              disabled={!!busy}
              onClick={() => void draft()}
            >
              {busy === 'draft' ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}{' '}
              {drafts ? 'Generate again' : 'Generate'}
            </button>
            <span className="text-xs text-page-muted">
              The AI reads the agent’s instructions, tools, guardrails and its {existing.length} existing case
              {existing.length === 1 ? '' : 's'}.
            </span>
          </div>
          {drafts?.length ? (
            <div className="grid gap-2" data-testid="evals-add-drafts">
              <div className="text-xs text-page-muted">
                {drafts.length} proposed — check what to keep; nothing is saved yet.
              </div>
              {drafts.map((d, i) => (
                <label
                  key={i}
                  className={
                    'rounded-md border border-card-border p-2 flex gap-2 items-start ' +
                    (d.keep ? '' : 'opacity-60')
                  }
                  data-testid="evals-add-draft"
                >
                  <input
                    type="checkbox"
                    checked={d.keep}
                    onChange={() => setDrafts(drafts.map((x, j) => (j === i ? { ...x, keep: !x.keep } : x)))}
                  />
                  <span className="min-w-0 text-sm">
                    <span className="text-page-text font-medium">{d.name}</span>
                    {d.similarTo ? (
                      <span className="ml-2 text-xs text-amber-700 dark:text-amber-300">
                        close to “{d.similarTo}”
                      </span>
                    ) : null}
                    <span className="block text-xs text-page-muted">“{d.prompt}”</span>
                    <span className="flex flex-wrap gap-1 mt-1">
                      {d.expect.map((c, j) => (
                        <span
                          key={j}
                          className="rounded-full border border-card-border px-2 py-0.5 text-[11px] text-page-muted"
                        >
                          {checkText(c)}
                        </span>
                      ))}
                    </span>
                  </span>
                </label>
              ))}
              <div className="flex flex-wrap gap-2 justify-end">
                <button
                  type="button"
                  className={btnGhost}
                  disabled={!kept.length || !!busy}
                  onClick={() => void save(kept, true)}
                >
                  Save {kept.length} &amp; run them
                </button>
                <button
                  type="button"
                  className={btnPrimary}
                  data-testid="evals-add-save"
                  disabled={!kept.length || !!busy}
                  onClick={() => void save(kept, false)}
                >
                  {busy === 'save' ? <Loader2 size={14} className="animate-spin" /> : null} Save {kept.length}{' '}
                  selected
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="grid gap-2">
          <label className="grid gap-1 text-xs text-page-muted">
            Name
            <input
              className={input}
              data-testid="evals-add-name"
              placeholder="what the case proves, e.g. saves nothing when asked to look"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <label className="grid gap-1 text-xs text-page-muted">
            Question to the agent
            <textarea
              className={input}
              rows={2}
              data-testid="evals-add-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />
          </label>
          <div className="grid gap-1 text-xs text-page-muted">
            A good answer…
            {rules.map((r, i) => {
              const k = KINDS.find((x) => x.id === r.kind)!;
              return (
                <div key={i} className="grid grid-cols-[14rem_minmax(0,1fr)_auto] gap-2 items-start">
                  <select
                    className={input}
                    value={r.kind}
                    onChange={(e) =>
                      setRules(rules.map((x, j) => (j === i ? { kind: e.target.value, value: '' } : x)))
                    }
                  >
                    {KINDS.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                  {k.needs === 'none' ? (
                    <span className="flex-1" />
                  ) : (
                    <span className="grid gap-0.5 min-w-0">
                      {k.needs === 'tool' && tools.length ? (
                        <select
                          className={input + (missing(r) ? ' border-red-500' : '')}
                          data-testid="evals-add-tool"
                          value={r.value}
                          onChange={(e) =>
                            setRules(rules.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))
                          }
                        >
                          <option value="">Pick a tool of the agent…</option>
                          {tools.map((t) => (
                            <option key={t.name} value={t.name}>
                              {t.name} — {t.note}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          className={
                            input + (missing(r) ? ' border-red-500 bg-red-50 dark:bg-red-950/30' : '')
                          }
                          data-testid="evals-add-value"
                          placeholder={
                            k.needs === 'tool'
                              ? 'tool name, e.g. save_screens'
                              : k.needs === 'seconds'
                                ? '60'
                                : k.needs === 'tokens'
                                  ? '30000'
                                  : 'text the answer must contain'
                          }
                          value={r.value}
                          onChange={(e) =>
                            setRules(rules.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))
                          }
                        />
                      )}
                      {missing(r) ? (
                        <span
                          className="text-[11px] text-red-600 dark:text-red-300"
                          data-testid="evals-add-missing"
                        >
                          Fill this in or remove the expectation
                        </span>
                      ) : null}
                    </span>
                  )}
                  <button
                    type="button"
                    className={btnGhost}
                    aria-label="Remove the expectation"
                    onClick={() => setRules(rules.filter((_, j) => j !== i))}
                  >
                    <X size={12} />
                  </button>
                </div>
              );
            })}
            <button
              type="button"
              className={btnGhost + ' w-fit'}
              onClick={() => setRules([...rules, { kind: 'has', value: '' }])}
            >
              + expectation
            </button>
          </div>
          <div className="flex flex-wrap gap-2 justify-end">
            <button
              type="button"
              className={btnGhost}
              disabled={!handReady || !!busy}
              onClick={() => void save([handCase()], true)}
            >
              Save &amp; run once
            </button>
            <button
              type="button"
              className={btnPrimary}
              data-testid="evals-add-save-hand"
              disabled={!handReady || !!busy}
              onClick={() => void save([handCase()], false)}
            >
              {busy === 'save' ? <Loader2 size={14} className="animate-spin" /> : null} Save
            </button>
          </div>
        </div>
      )}
      {err ? (
        <div className="text-sm text-red-600" data-testid="evals-add-error">
          {err}
        </div>
      ) : null}
    </div>
  );
}
